// src/components/SeoDebug.js
// Shows a debug panel in development mode displaying all SEO meta for the page.

import React, { useState } from "react";

export default function SeoDebug({ seo }) {
  const [open, setOpen] = useState(false);

  if (process.env.NODE_ENV === "production") return null;

  if (!seo) {
    return <div style={badge("#ef4444")}>SEO: no data</div>;
  }

  const { meta = {}, openGraph = {}, twitter = {}, jsonLd } = seo;
  const page = seo.page || {};
  const site = seo.site || {};

  return (
    <>
      <button onClick={() => setOpen(!open)} style={badge("#a3e635", "#000")}>
        SEO Debug {open ? "x" : "o"}
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            bottom: 48,
            right: 16,
            width: 420,
            maxHeight: "80vh",
            overflowY: "auto",
            background: "#0d0f12",
            border: "1px solid #303747",
            borderRadius: 12,
            padding: 20,
            zIndex: 9999,
            fontFamily: "DM Mono, monospace",
            fontSize: 12,
            color: "#f0f2f5",
            boxShadow: "0 8px 32px rgba(0,0,0,.8)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>SEO Inspector</span>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none",
                border: "none",
                color: "#8b93a5",
                cursor: "pointer",
                fontSize: 16,
              }}
            >
              x
            </button>
          </div>

          <Section label="PRIMARY META" color="#a3e635">
            <Row label="Title" value={meta.title} max={60} />
            <Row label="Description" value={meta.description} max={160} />
            <Row label="Keywords" value={meta.keywords?.join(", ")} />
            <Row label="Robots" value={meta.robots} />
            <Row label="Canonical" value={meta.canonical} />
          </Section>

          <Section label="OPEN GRAPH" color="#60a5fa">
            <Row label="OG Title" value={openGraph.title} />
            <Row label="OG Description" value={openGraph.description} />
            <Row label="OG Image" value={openGraph.image} />
            <Row label="OG Type" value={openGraph.type} />
            <Row label="OG URL" value={openGraph.url} />
            <Row label="Site Name" value={openGraph.siteName} />
          </Section>

          <Section label="TWITTER CARD" color="#60a5fa">
            <Row label="Card" value={twitter.card} />
            <Row label="Title" value={twitter.title} />
            <Row label="Description" value={twitter.description} />
            <Row label="Image" value={twitter.image} />
            <Row label="Site" value={twitter.site} />
          </Section>

          {jsonLd && (
            <Section label="JSON-LD SCHEMA" color="#a78bfa">
              <Row label="Type" value={jsonLd["@type"]} />
              <pre
                style={{
                  background: "#1a1e26",
                  borderRadius: 6,
                  padding: "8px 10px",
                  fontSize: 10,
                  overflowX: "auto",
                  margin: "6px 0 0",
                  color: "#8b93a5",
                }}
              >
                {JSON.stringify(jsonLd, null, 2)}
              </pre>
            </Section>
          )}

          <Section label="PAGE INFO" color="#2dd4bf">
            <Row label="Slug" value={page.slug} />
            <Row label="Type" value={page.pageType} />
            <Row label="Status" value={page.status} />
            <Row label="Site" value={site.name} />
          </Section>
        </div>
      )}
    </>
  );
}

function badge(bg, color = "#fff") {
  return {
    position: "fixed",
    bottom: 16,
    right: 16,
    zIndex: 9999,
    background: bg,
    color,
    border: "none",
    borderRadius: 99,
    padding: "6px 12px",
    fontSize: 12,
    fontFamily: "monospace",
    cursor: "pointer",
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(0,0,0,.4)",
  };
}

function Section({ label, color, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color, fontSize: 10, letterSpacing: 1, fontWeight: 600, marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          background: "#1a1e26",
          borderRadius: 8,
          padding: "8px 10px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, max }) {
  if (!value) {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <span style={{ color: "#525d73", minWidth: 110 }}>{label}</span>
        <span style={{ color: "#f87171", fontSize: 11 }}>missing</span>
      </div>
    );
  }

  const len = value.length;
  const over = max && len > max;

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <span style={{ color: "#8b93a5", minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ color: over ? "#fbbf24" : "#f0f2f5", wordBreak: "break-all" }}>
        {value}
        {max && (
          <span style={{ color: over ? "#f87171" : "#525d73", marginLeft: 4 }}>
            ({len}/{max})
          </span>
        )}
      </span>
    </div>
  );
}
